import React from 'react';
import { i18next } from '@yola/ws-sdk';
import preloadImage from 'src/js/modules/utils/preload-image';
import elementSettings from 'src/js/modules/element-settings';
import buildDividerOption from 'src/js/modules/element-settings/helpers/build-divider-option';
import ColumnsAlignmentsOption from '../components/columns-alignments-option';
import ColumnsAmountOption from '../components/columns-amount-option';
import computeBorderRadiusFallback from '../helpers/compute-border-radius-fallback';
import computeSpacingFallback from '../helpers/compute-spacing-fallback';
import createAdjustedEventTraitsGetter from '../helpers/create-adjusted-event-traits-getter';
import prepareInstructionsForColumnWidthReset from '../helpers/prepare-instructions-for-column-width-reset';
import {
  getCornersAdjustedEventTraits,
  getCornersModifiedEventTraits,
} from '../helpers/get-corners-event-traits';
import {
  getSpacingAdjustedEventTraits,
  getSpacingModifiedEventTraits,
} from '../helpers/get-spacing-event-traits';
import previewGifs, { columnsPreviewGifs } from '../constants/preview-gifs';
import ids from '../constants/ids';
import getHorizontalContentAlignment from './horizontal-content-alignment';
import getVerticalContentAlignment from './vertical-content-alignment';
import getBorderRadiuses from './border-radiuses';
import getPaddings from './paddings';
import getGaps from './gaps';

const {
  constants: { optionTypes, triggerTypes },
} = elementSettings;

const EVENT_TRAIT_PREFIX = 'columns';

const getAllColumnsSettings = () => {
  Object.values(columnsPreviewGifs).forEach((gif) => preloadImage(gif));

  return {
    id: ids.ALL_COLUMNS,
    triggerOn: triggerTypes.HOVER,
    priority: 1,
    eventTraitPrefix: EVENT_TRAIT_PREFIX,
    settings: {
      parentSelector: 'ws-block[src*="/custom/"]',
      querySelector: 'ws-columns',
      columnQuerySelector: 'ws-column',
    },
    matches(element, config) {
      const { querySelector, columnQuerySelector, parentSelector } = config;
      return (
        element.matches(`${querySelector}, ${columnQuerySelector}`) &&
        element.closest(parentSelector)
      );
    },
    getTargetElement(element, config) {
      const { querySelector, columnQuerySelector } = config;
      if (element.matches(columnQuerySelector)) {
        return element.closest(querySelector);
      }

      return element;
    },
    tabs: [
      {
        id: 'appearance',
        accordionTitle: i18next.t('All columns'),
        options: [
          {
            id: 'alignments',
            type: optionTypes.CUSTOM,
            component: <ColumnsAlignmentsOption />,
            getAdjustedEventTraits: createAdjustedEventTraitsGetter('Alignment'),
          },
          {
            id: 'color-scheme',
            type: optionTypes.COLOR_SCHEME,
            title: i18next.t('Color scheme'),
            getAdjustedEventTraits: createAdjustedEventTraitsGetter('Surfaces'),
            popover: {
              content: i18next.t('Change background of all columns'),
              imgSrc: previewGifs.COLUMNS_BACKGROUND_GIF,
            },
            choices: [
              {
                id: 'ws-light-0',
                value: 'ws-light-0',
              },
              {
                id: 'ws-light-1',
                value: 'ws-light-1',
              },
              {
                id: 'ws-dark-0',
                value: 'ws-dark-0',
              },
              {
                id: 'ws-dark-1',
                value: 'ws-dark-1',
              },
            ],
            querySelector: 'ws-column',
            attributes: ['surface'],
          },
          {
            id: 'content-alignment',
            type: optionTypes.SECTION,
            title: i18next.t('Content alignment'),
            options: [
              getHorizontalContentAlignment({
                querySelector: 'ws-column',
                popover: {
                  content: i18next.t('Align the content inside columns horizontally'),
                  imgSrc: previewGifs.COLUMNS_HORIZONTAL_CONTENT_ALIGNMENT_GIF,
                },
              }),
              getVerticalContentAlignment({
                querySelector: 'ws-column',
                popover: {
                  content: i18next.t('Align the content inside columns vertically'),
                  imgSrc: previewGifs.COLUMNS_VERTICAL_CONTENT_ALIGNMENT_GIF,
                },
              }),
            ],
          },
          {
            id: 'columns-amount',
            type: optionTypes.CUSTOM,
            component: <ColumnsAmountOption />,
            getAdjustedEventTraits: createAdjustedEventTraitsGetter('PerRow'),
            popover: {
              content: i18next.t('Select the number of columns per row'),
              imgSrc: previewGifs.COLUMNS_AMOUNT_GIF,
            },
          },
          {
            id: 'column-width',
            type: optionTypes.SELECT,
            title: i18next.t('Column width'),
            attributes: ['width'],
            getAdjustedEventTraits: createAdjustedEventTraitsGetter('Width'),

            choices: [
              { name: i18next.t('Fixed'), value: 'fixed' },
              { name: i18next.t('Fill'), value: 'fill' },
            ],
            onChange: (value, option) => {
              if (value === 'fixed') {
                return null;
              }

              const { elementIds } = option;

              return prepareInstructionsForColumnWidthReset(elementIds[0]);
            },
            popover: {
              content: i18next.t('Stretch columns to the full width of the container'),
              imgSrc: previewGifs.COLUMNS_WIDTH_GIF,
            },
          },
          {
            id: 'column-height',
            type: optionTypes.SELECT,
            title: i18next.t('Column height'),
            attributes: ['height'],
            getAdjustedEventTraits: createAdjustedEventTraitsGetter('Height'),
            choices: [
              { name: i18next.t('Hug'), value: 'hug' },
              { name: i18next.t('Fill'), value: 'fill' },
            ],
            popover: {
              content: i18next.t('Stretch columns to match the tallest one'),
              imgSrc: previewGifs.COLUMNS_HEIGHT_GIF,
            },
          },
          {
            id: 'border',
            type: optionTypes.TOGGLE,
            title: i18next.t('Border'),
            querySelector: 'ws-column',
            attributes: ['border'],
          },
          {
            id: 'shadow',
            type: optionTypes.TOGGLE,
            title: i18next.t('Shadow'),
            querySelector: 'ws-column',
            attributes: ['shadow'],
          },
          {
            id: 'corners',
            type: optionTypes.TOGGLEABLE_SECTION,
            title: i18next.t('Corners'),
            getAdjustedEventTraits: getCornersAdjustedEventTraits,
            getModifiedEventTraits: getCornersModifiedEventTraits,
            computeFallbackValue: computeBorderRadiusFallback,
            options: [
              getBorderRadiuses({
                querySelector: 'ws-column',
                getAdjustedEventTraits: getCornersAdjustedEventTraits,
                getModifiedEventTraits: getCornersModifiedEventTraits,
              }),
            ],
          },
          {
            id: 'spacing',
            type: optionTypes.TOGGLEABLE_SECTION,
            title: i18next.t('Spacing'),
            getAdjustedEventTraits: getSpacingAdjustedEventTraits,
            getModifiedEventTraits: getSpacingModifiedEventTraits,
            computeFallbackValue: computeSpacingFallback,
            options: [
              ...getPaddings({
                querySelector: 'ws-column',
                getAdjustedEventTraits: getSpacingAdjustedEventTraits,
                getModifiedEventTraits: getSpacingModifiedEventTraits,
              }),
              buildDividerOption(),
              ...getGaps({
                querySelector: 'ws-columns',
                getAdjustedEventTraits: getSpacingAdjustedEventTraits,
                getModifiedEventTraits: getSpacingModifiedEventTraits,
              }),
            ],
          },
        ],
      },
    ],
  };
};
export default getAllColumnsSettings;
